/**
 * Auth Store
 *
 * Pinia store for authentication state management.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import router from '@v2/router';
import { authService } from '../services/auth.service';
import { registerTokenRefresher, registerSessionExpiredHandler } from '@v2/services/api/client';
import { useUserStore } from './user.store';
import type { TokenClaims, AuthInitState, ImpersonationState, Visitor, PublicLinkContext } from '../types/auth';
import { AuthError } from '../types/errors';

/**
 * Auth store for managing authentication state
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(null);
  const claims = ref<TokenClaims | null>(null);
  const initState = ref<AuthInitState>('uninitialized');
  const publicView = ref(false);
  const impersonation = ref<ImpersonationState>({
    active: false,
    originalToken: null,
    returnPath: null,
  });
  const visitor = ref<Visitor | null>(null);
  const publicLinkContext = ref<PublicLinkContext>({
    documentId: null,
    debateId: null,
  });

  // Initialization promise for idempotent init
  let initPromise: Promise<void> | null = null;

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const isInitialized = computed(() => initState.value === 'initialized');
  const isInitializing = computed(() => initState.value === 'initializing');
  const isImpersonating = computed(() => impersonation.value.active);

  // Register token refresher so the API client can silently retry on 401
  registerTokenRefresher(async () => {
    const result = await authService.refreshToken();
    token.value = result.token;
    claims.value = result.claims;
    return result.token;
  });

  // On unrecoverable 401, clear state and redirect to login
  registerSessionExpiredHandler(() => {
    token.value = null;
    claims.value = null;
    const userStore = useUserStore();
    userStore.clear();
    router.push('/login');
  });

  /**
   * Initialize auth - idempotent, safe to call multiple times
   */
  async function initialize(): Promise<void> {
    // Already initialized
    if (initState.value === 'initialized') {
      return;
    }

    // Public view doesn't need auth
    if (publicView.value) {
      initState.value = 'initialized';
      return;
    }

    // Return existing promise if initializing
    if (initPromise) {
      return initPromise;
    }

    initState.value = 'initializing';
    console.debug('[AuthStore] Initializing...');

    initPromise = (async () => {
      try {
        const result = await authService.initialize();

        if (result) {
          token.value = result.token;
          claims.value = result.claims;

          // Load user data after auth
          const userStore = useUserStore();
          await Promise.all([
            userStore.loadProfile(),
            userStore.loadNotifications(),
          ]);
        }

        initState.value = 'initialized';
        console.debug('[AuthStore] Initialized', { authenticated: !!result });
      } catch (error) {
        console.error('[AuthStore] Initialization failed:', error);
        initState.value = 'initialized'; // Mark as initialized even on error
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  }

  /**
   * Sign in via popup
   * @param idpHint - Optional identity provider hint (e.g. 'google.com') to skip B2C provider selection
   */
  async function login(idpHint?: string): Promise<boolean> {
    try {
      const result = await authService.signIn(idpHint);
      token.value = result.token;
      claims.value = result.claims;

      // Load user data
      const userStore = useUserStore();
      await Promise.all([
        userStore.loadProfile(),
        userStore.loadNotifications(),
      ]);

      console.debug('[AuthStore] Login successful');
      return true;
    } catch (error) {
      console.error('[AuthStore] Login failed:', error);
      throw error;
    }
  }

  /**
   * Sign out
   */
  async function logout(): Promise<void> {
    // Clear impersonation if active
    if (impersonation.value.active) {
      impersonation.value = {
        active: false,
        originalToken: null,
        returnPath: null,
      };
    }

    // Clear user data
    const userStore = useUserStore();
    userStore.clear();

    // Clear auth state
    token.value = null;
    claims.value = null;

    // Sign out from B2C (will redirect to home via postLogoutRedirectUri)
    await authService.signOut();
  }

  /**
   * Start impersonating another user (admin only)
   */
  async function startImpersonation(userId: string, targetPath: string = '/'): Promise<void> {
    if (!token.value) {
      throw new AuthError('Must be authenticated to impersonate', 'SESSION_NOT_FOUND');
    }

    try {
      // Save current admin state
      const currentPath = router.currentRoute.value.fullPath;
      impersonation.value = {
        active: false, // Will set to true after success
        originalToken: token.value,
        returnPath: currentPath,
      };

      // Get impersonation token
      const impersonationToken = await authService.getImpersonationToken(userId);

      // Switch to impersonation token
      token.value = impersonationToken;

      // Try to decode claims from impersonation token
      try {
        const base64Url = impersonationToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        claims.value = JSON.parse(jsonPayload);
      } catch {
        claims.value = null;
      }

      impersonation.value.active = true;

      // Reload user profile as impersonated user
      const userStore = useUserStore();
      await userStore.loadProfile();

      // Navigate to target
      router.push(targetPath);
      console.debug('[AuthStore] Impersonation started');
    } catch (error) {
      // Restore original state on failure
      impersonation.value = {
        active: false,
        originalToken: null,
        returnPath: null,
      };
      throw error;
    }
  }

  /**
   * Stop impersonation and return to admin
   */
  async function stopImpersonation(): Promise<void> {
    if (!impersonation.value.active || !impersonation.value.originalToken) {
      throw new AuthError('Not currently impersonating', 'SESSION_NOT_FOUND');
    }

    try {
      const returnPath = impersonation.value.returnPath || '/';

      // Restore admin token
      token.value = impersonation.value.originalToken;

      // Try to decode claims from admin token
      try {
        const base64Url = token.value.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        claims.value = JSON.parse(jsonPayload);
      } catch {
        claims.value = null;
      }

      // Clear impersonation state
      impersonation.value = {
        active: false,
        originalToken: null,
        returnPath: null,
      };

      // Reload admin profile
      const userStore = useUserStore();
      await userStore.loadProfile();

      // Navigate back
      router.push(returnPath);
      console.debug('[AuthStore] Impersonation stopped');
    } catch (error) {
      // Clear impersonation state even on error
      impersonation.value = {
        active: false,
        originalToken: null,
        returnPath: null,
      };
      throw error;
    }
  }

  /**
   * Set public view mode
   */
  function setPublicView(value: boolean): void {
    publicView.value = value;
    if (value) {
      initState.value = 'initialized';
    }
  }

  /**
   * Set visitor info for public access
   */
  function setVisitor(info: Visitor | null): void {
    visitor.value = info;
  }

  /**
   * Set public link context
   */
  function setPublicLinkContext(context: Partial<PublicLinkContext>): void {
    publicLinkContext.value = {
      ...publicLinkContext.value,
      ...context,
    };
  }

  /**
   * Refresh token
   */
  async function refreshToken(): Promise<void> {
    const result = await authService.refreshToken();
    token.value = result.token;
    claims.value = result.claims;
  }

  return {
    // State
    token,
    claims,
    initState,
    publicView,
    impersonation,
    visitor,
    publicLinkContext,

    // Getters
    isAuthenticated,
    isInitialized,
    isInitializing,
    isImpersonating,

    // Actions
    initialize,
    login,
    logout,
    startImpersonation,
    stopImpersonation,
    setPublicView,
    setVisitor,
    setPublicLinkContext,
    refreshToken,
  };
});

/**
 * Get auth token synchronously (for API interceptors)
 * This function can be called outside of Vue components
 */
export function getAuthToken(): string | null {
  // Access the store directly from pinia
  // This works because pinia stores state in a reactive object
  const store = useAuthStore();
  return store.token;
}
